/* eslint-disable react/require-default-props */
import PropTypes from 'prop-types';
import React, { useState, useRef, useEffect, Fragment } from 'react';
import _ from 'lodash';
import { useDispatch, useSelector } from '../../common/react-redux-hooks';
import * as cacActions from '../../../actions/cacActions';
import { getApiRequestParameter } from '../../../util/cacUtils';
import { HANDLE_CORR_CHANGE_LABEL } from '../CacConstants';
import CorrScatterChart from './CorrScatterChart';
import InfoBox, {
  InfoBoxSpacer,
  IBButton,
} from '../../common/EnhancedGridFeature/InfoBox';
import CacToggleLabel from '../enhancedFeatures/CacToggleLabel';
import useResizeObserver from '../chart/useResizeObserver';

const CorrClusterView = props => {
  const {
    id,
    zoomType,
    redrawChart,
    project: {
      projectId,
      corrResponse: { cm },
      mapOptions: {
        frontNodeLabelPositions,
        frontNodeLabelPositionsCentered,
        topNodeLabelPositions,
        topNodeLabelPositionsCentered,
        sideNodeLabelPositions,
        sideNodeLabelPositionsCentered,
        showContributionScores,
        isCenterChart,
        showPassive,
        isAxisVisible,
        areRowsVisible,
        areColsVisible,
        showRowLongNames,
        showColLongNames,
        showPassiveLongNames,
      },
      passiveCols,
      passiveRows,
      chartView,
      updateNodes,
      nodeColors,
      rows,
      columns,
    },
  } = props;

  const wrapperRef = useRef();
  const [dimensions, setDimensions] = useState({ height: 0, width: 0 });
  const [forceUpdate, setForceUpdate] = useState(null);

  const dispatch = useDispatch();
  const { userId, currentAccountId } = useSelector(
    state => state.login.loginResponse.data
  );


  const toggleSetLNCol = val => {
    dispatch(cacActions.setCacShowColLongNames(projectId, val));
  };

  const toggleSetLNRow = val => {
    dispatch(cacActions.setCacShowRowLongNames(projectId, val));
  };

  const toggleSetLNPassive = val => {
    dispatch(cacActions.setCacShowPassiveLongNames(projectId, val));
  };

  const setShowPassive = passive => {
    dispatch(cacActions.setCacShowPassives(projectId, passive));
  };
  const setIsCenterChart = centered => {
    dispatch(cacActions.setCacCenterChart(projectId, centered));
  };
  const setIsAxisVisible = axisVisible => {
    dispatch(cacActions.setCacIsAxisVisible(projectId, axisVisible));
  };
  const setAreRowsVisible = rowsVisible => {
    dispatch(cacActions.setCacAreRowsVisible(projectId, rowsVisible));
  };
  const setAreColsVisible = colsVisible => {
    dispatch(cacActions.setCacAreColsVisible(projectId, colsVisible));
  };
  const setShowContScores = showScores => {
    dispatch(cacActions.setCacShowContributionScores(projectId, showScores));
  };
  const setCacProjectSavedFlag = isProjectSaved => {
    dispatch(cacActions.setCacProjectSavedFlag(projectId, isProjectSaved));
  };
  const editRowColTitle = (newTitle, nodeType, nodeIndex) => {
    dispatch(cacActions.editRowColTitle(projectId, newTitle, nodeType, nodeIndex));
    const projectObj = getApiRequestParameter(
      props.project,
      `${nodeType}♥${newTitle}`,
      nodeIndex,
      HANDLE_CORR_CHANGE_LABEL
    );
    dispatch(
      cacActions.getClusterProjectDetails({
        userId,
        currentAccountId,
        ...projectObj,
      })
    );

    

  }
  // const restoreRowColTitle = (nodeType, nodeIndex) => {
  //   dispatch(cacActions.restoreRowColTitle(projectId, nodeType, nodeIndex));
  // };

  const updateNodeLabelPositions = nlPositions => {
    const cview = _.isEqual(chartView, [1, 2])
      ? 'front'
      : _.isEqual(chartView, [1, 3])
      ? 'top'
      : 'side'; // [2,3]
    dispatch(
      cacActions.updateCacNodeLabelPositions(
        projectId,
        nlPositions,
        isCenterChart,
        cview
      )
    );
  };
  const resizeObserver = useResizeObserver(wrapperRef);

  useEffect(() => {
    if (resizeObserver) {
      setDimensions(resizeObserver);
    } else if (wrapperRef && wrapperRef.current) {
      setDimensions({
        height: wrapperRef.current.clientHeight,
        width: wrapperRef.current.clientWidth,
      });
    }
  }, [resizeObserver, wrapperRef]);

  const resetChart = () => {
    setForceUpdate({});
  };

  const getNodeLabelPositions = () => {
    return _.isEqual(chartView, [1, 2])
      ? isCenterChart
        ? frontNodeLabelPositionsCentered
        : frontNodeLabelPositions
      : _.isEqual(chartView, [1, 3])
      ? isCenterChart
        ? topNodeLabelPositionsCentered
        : topNodeLabelPositions
      : isCenterChart // [2,3]
      ? sideNodeLabelPositionsCentered
      : sideNodeLabelPositions
  };

  const buildInfoBox = () => {
    // backwards compatable
    const rv = _.isUndefined(areRowsVisible) ? true : areRowsVisible;
    const cv = _.isUndefined(areColsVisible) ? true : areColsVisible;

    const ibp = [];
    ibp.push(
      <CacToggleLabel
        id="forCol"
        label="Show Full Column Labels"
        check={showColLongNames}
        chartUpdate={toggleSetLNCol}
      />
    );
    ibp.push(
      <CacToggleLabel
        id="forRow"
        label="Show Full Row Labels"
        check={showRowLongNames}
        chartUpdate={toggleSetLNRow}
      />
    );
    ibp.push(
      <CacToggleLabel
        id="forPas"
        label="Show Full Passive Labels"
        check={showPassiveLongNames}
        chartUpdate={toggleSetLNPassive}
      />
    );
    ibp.push(
      <CacToggleLabel
        id="showPass"
        label="Hide Passives"
        check={!showPassive}
        chartUpdate={b => {
          setShowPassive(!b);
        }}
      />
    );
    ibp.push(
      <CacToggleLabel
        id="contScore"
        label="Contribution Scores"
        check={showContributionScores}
        chartUpdate={setShowContScores}
      />
    );
    ibp.push(
      <CacToggleLabel
        id="setCC"
        label="Center Chart"
        check={isCenterChart}
        chartUpdate={setIsCenterChart}
      />
    );
    ibp.push(
      <CacToggleLabel
        id="setAxVis"
        label="Hide Axis"
        check={!isAxisVisible}
        chartUpdate={b => {
          setIsAxisVisible(!b);
        }}
      />
    );
    ibp.push(
      <CacToggleLabel
        id="setRowsVis"
        label="Hide All 
        Rows"
        check={!rv}
        chartUpdate={b => {
          setAreRowsVisible(!b);
        }}
      />
    );
    ibp.push(
      <CacToggleLabel
        id="setColsVis"
        label="Hide All 
        Columns"
        check={!cv}
        chartUpdate={b => {
          setAreColsVisible(!b);
        }}
      />
    );
    ibp.push(
      <InfoBoxSpacer Height="0.1em" boxClassName="toolPalette-spacer-row" />
    );
    ibp.push(
      <IBButton
        buttonText="Reset Label Positions"
        handleButtonClick={resetChart}
      />
    );
    return ibp;
  };

  return (
    // eslint-disable-next-line react/jsx-fragments
    <Fragment>
      <div className="outerDiv" id="correspondenceClusterChart">
        <div className="corrD3_wrapperDiv">
          <div className="corrD3_innerDiv">
            <div className="corrD3_infoBox_div">
              <InfoBox
                Panels={buildInfoBox()}
                updatePanels={buildInfoBox}
                Positions={['bottom']}
                Align="end"
                InfoBoxButtonInner={
                  <span className="ibox-cac-ellipsis">
                    <svg
                      className="cac-ellipsis"
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                    >
                      <circle fill="#757575" cx="03" cy="10" r="2" />
                      <circle fill="#757575" cx="10" cy="10" r="2" />
                      <circle fill="#757575" cx="17" cy="10" r="2" />
                    </svg>
                  </span>
                }
                containerClassName="cac-popover-container"
              />
            </div>
            <div style={{ height: '100%' }}>
              <div
                ref={wrapperRef}
                className="cac-chart-wrapper"
                onContextMenu={e => {
                  // prevent windows menu
                  e.preventDefault();
                }}
              >
                <CorrScatterChart
                  data-test="scatterChart"
                  projectId={projectId}
                  dimensions={dimensions}
                  isCenterChart={isCenterChart}
                  showPassive={showPassive}
                  isAxisVisible={isAxisVisible}
                  areRowsVisible={areRowsVisible}
                  areColsVisible={areColsVisible}
                  showLongNames={{
                    forRow: showRowLongNames,
                    forCol: showColLongNames,
                    forPassive: showPassiveLongNames,
                  }}
                  chartView={chartView}
                  updateNodes={
                    updateNodes || redrawChart || forceUpdate !== null
                  }
                  setForceUpdate={setForceUpdate}
                  nodeColors={nodeColors}
                  showContScores={showContributionScores}
                  id={id}
                  zoomType={zoomType}
                  updateNodeLabelPositions={updateNodeLabelPositions}
                  nodeLabelPositions={getNodeLabelPositions()}
                  cm={cm}
                  rows={rows}
                  columns={columns}
                  passiveRows={passiveRows}
                  passiveCols={passiveCols}
                  setProjectSavedFlag={setCacProjectSavedFlag}
                  editRowColTitle={editRowColTitle}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

CorrClusterView.propTypes = {
  redrawChart: PropTypes.bool,
  project: PropTypes.shape({
    projectId: PropTypes.number,
    corrResponse: PropTypes.shape({
      cm: PropTypes.shape({}),
    }),
    mapOptions: PropTypes.shape({
      frontNodeLabelPositions: PropTypes.arrayOf(PropTypes.shape({})),
      frontNodeLabelPositionsCentered: PropTypes.arrayOf(PropTypes.shape({})),
      topNodeLabelPositions: PropTypes.arrayOf(PropTypes.shape({})),
      topNodeLabelPositionsCentered: PropTypes.arrayOf(PropTypes.shape({})),
      sideNodeLabelPositions: PropTypes.arrayOf(PropTypes.shape({})),
      sideNodeLabelPositionsCentered: PropTypes.arrayOf(PropTypes.shape({})),
      showContributionScores: PropTypes.bool,
      isCenterChart: PropTypes.bool,
      showPassive: PropTypes.bool,
      isAxisVisible: PropTypes.bool,
      areRowsVisible: PropTypes.bool,
      areColsVisible: PropTypes.bool,
      showRowLongNames: PropTypes.bool,
      showColLongNames: PropTypes.bool,
      showPassiveLongNames: PropTypes.bool,
    }),
    passiveCols: PropTypes.arrayOf(PropTypes.number),
    passiveRows: PropTypes.arrayOf(PropTypes.number),
    chartView: PropTypes.arrayOf(PropTypes.number),
    updateNodes: PropTypes.bool,
    nodeColors: PropTypes.shape({}),
  }),
  id: PropTypes.string,
  zoomType: PropTypes.string,
};

export default CorrClusterView;
